import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Check, X, LayoutDashboard, Receipt, Users, Target, Wallet, FileText, Settings, ClipboardList, UserCog, Building2 } from 'lucide-react';

export default function Roles({ user, onLogout }) {
  const roles = [
    {
      name: 'Admin',
      value: 'admin',
      description: 'Full system access with all administrative privileges',
      color: 'bg-purple-100 text-purple-700 border-purple-200',
      permissions: {
        dashboard: true,
        properties: true,
        customers: true,
        billing: true,
        plans: true,
        leads: true,
        expenses: true,
        reports: true,
        users: true,
        roles: true,
        settings: true
      }
    },
    {
      name: 'Sales',
      value: 'sales',
      description: 'Access to lead management and customer data',
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      permissions: {
        dashboard: true,
        properties: false,
        customers: true,
        billing: false,
        plans: false,
        leads: true,
        expenses: false,
        reports: false,
        users: false,
        roles: false,
        settings: true
      }
    },
    {
      name: 'Billing',
      value: 'billing',
      description: 'Access to billing, bookings and payment management',
      color: 'bg-green-100 text-green-700 border-green-200',
      permissions: {
        dashboard: true,
        properties: false,
        customers: true,
        billing: true,
        plans: true,
        leads: false,
        expenses: false,
        reports: false,
        users: false,
        roles: false,
        settings: true
      }
    },
    {
      name: 'Customer',
      value: 'customer',
      description: 'Customer portal access only - view own bookings',
      color: 'bg-slate-100 text-slate-700 border-slate-200',
      permissions: {
        dashboard: false,
        properties: false,
        customers: false,
        billing: false,
        plans: false,
        leads: false,
        expenses: false,
        reports: false,
        users: false,
        roles: false,
        settings: false,
        customer_portal: true
      }
    }
  ];

  const permissionLabels = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'properties', label: 'Properties', icon: Building2 },
    { key: 'customers', label: 'Customers', icon: Users },
    { key: 'billing', label: 'Billing', icon: Receipt },
    { key: 'plans', label: 'Installment Plans', icon: ClipboardList },
    { key: 'leads', label: 'Leads', icon: Target },
    { key: 'expenses', label: 'Expenses', icon: Wallet },
    { key: 'reports', label: 'Reports', icon: FileText },
    { key: 'users', label: 'User Management', icon: UserCog },
    { key: 'roles', label: 'Roles', icon: Shield },
    { key: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="roles-page">
        {/* Header */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Shield className="text-emerald-600" />
                Roles & Permissions
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">View role-based access control settings</p>
            </div>
          </CardHeader>
        </Card>

        {/* Roles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {roles.map((role) => (
            <Card key={role.value} className="overflow-hidden" data-testid={`role-card-${role.value}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className={role.color}>{role.name}</Badge>
                    <p className="text-sm text-slate-500 mt-2">{role.description}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs font-semibold text-slate-400 uppercase mb-3">Permissions</p>
                <div className="grid grid-cols-2 gap-2">
                  {permissionLabels.map(({ key, label, icon: Icon }) => {
                    const hasAccess = role.permissions[key];
                    if (role.value === 'customer' && key !== 'customer_portal') {
                      return null;
                    }
                    return (
                      <div
                        key={key}
                        className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
                          hasAccess 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-slate-50 text-slate-400'
                        }`}
                      >
                        {hasAccess ? (
                          <Check size={14} className="text-green-600" />
                        ) : (
                          <X size={14} className="text-slate-400" />
                        )}
                        <Icon size={14} />
                        <span className="truncate">{label}</span>
                      </div>
                    );
                  })}
                  {role.value === 'customer' && (
                    <div className="flex items-center gap-2 p-2 rounded-lg text-sm bg-green-50 text-green-700 col-span-2">
                      <Check size={14} className="text-green-600" />
                      <Users size={14} />
                      <span>Customer Portal Access</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Shield className="text-blue-600 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-blue-800">Role Assignment</p>
                <p className="text-sm text-blue-600 mt-1">
                  Roles are assigned when creating new users. Go to User Management to create users with specific roles.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
